import { toast } from "react-toastify";
import { requestBackendReportExport } from "./backendReportExport";

export const CAMPAIGN_REPORT_TYPE = "CAMPAIGN_REPORT";
const CAMPAIGN_CUBE = "ClientDailyCampaignAmazonTable";

const CUBE_MEASURE_TO_API = {
  campaignBudget: "campaign_budget",
  cost: "spends",
  costDRR: "spends_drr",
  impressions: "impressions",
  impressionsDRR: "impressions_drr",
  clicks: "clicks",
  clicksDRR: "clicks_drr",
  ctr: "ctr",
  ctrDRR: "ctr_drr",
  cpc: "cpc",
  cpcDRR: "cpc_drr",
  cpm: "cpm",
  cpmDRR: "cpm_drr",
  conversions14d: "orders",
  conversions14dDRR: "orders_drr",
  conversion_rate: "cvr",
  conversion_rateDRR: "cvr_drr",
  sales14d: "revenue",
  sales14dDRR: "revenue_drr",
  average_order_value: "aov",
  average_order_valueDRR: "aov_drr",
  acos: "acos",
  acosDRR: "acos_drr",
  roas: "roas",
  roasDRR: "roas_drr",
};

const CUBE_DIMENSION_TO_API = {
  campaignNameDimension: "campaign_name",
  campaignIdDimension: "campaign_id",
  portfolioNameDimension: "portfolio_name",
  portfolioIdDimension: "portfolio_id",
  campaignTypeDimension: "campaign_type",
  targetingTypeDimension: "targeting_type",
  productSetDimension: "product_set",
  campaignFormatDimension: "campaign_format",
  campaignStatusDimension: "campaign_status",
  weekDimension: "week",
  monthDimension: "month",
  obDate: "date",
};

function stripTablePrefix(value = "") {
  return value.replace(`${CAMPAIGN_CUBE}.`, "");
}

function cubeMeasureToApi(measureKey) {
  return CUBE_MEASURE_TO_API[measureKey] ?? measureKey;
}

function cubeDimensionToApi(dimensionKey) {
  return CUBE_DIMENSION_TO_API[dimensionKey] ?? dimensionKey;
}

export function parseCampaignReportCubeFilters(
  cubeQueryFilters = [],
  portfoliosData = []
) {
  return cubeQueryFilters.map((filter) => {
    if (filter?.member === `${CAMPAIGN_CUBE}.portfolioNameDimension`) {
      return {
        member: `${CAMPAIGN_CUBE}.portfolioIdDimension`,
        operator: filter.operator,
        values: (filter.values ?? [])
          .map(
            (portfolio) =>
              portfoliosData?.find((portfolioData) => portfolioData.name === portfolio)
                ?.platform_id
          )
          .filter(Boolean),
      };
    }
    return filter;
  });
}

export function getCampaignReportVisibleColumnKeys({
  cubeQueryMeasures = [],
  cubeQueryDimensions = [],
}) {
  const dimensionKeys = cubeQueryDimensions.map((dimension) =>
    cubeDimensionToApi(stripTablePrefix(dimension))
  );
  const measureKeys = cubeQueryMeasures.map((measure) =>
    cubeMeasureToApi(stripTablePrefix(measure))
  );

  return [...dimensionKeys, ...measureKeys].filter(Boolean);
}

export function buildCampaignReportExportQueryBody({
  cubeQueryMeasures = [],
  cubeQueryDimensions = [],
  cubeQueryOrder = {},
  cubeQueryFilters = [],
  cubeQueryTimeDimensions = [],
}) {
  const timeDimensions = cubeQueryTimeDimensions.map((timeDimension) => {
    const formatted = { dimension: timeDimension.dimension };

    if (timeDimension.compareDateRange) {
      formatted.compareDateRange = timeDimension.compareDateRange;
    } else if (timeDimension.dateRange) {
      formatted.dateRange = timeDimension.dateRange;
    }

    return formatted;
  });

  return {
    cube_query: {
      measures: cubeQueryMeasures,
      dimensions: cubeQueryDimensions,
      filters: cubeQueryFilters,
      timeDimensions,
      order: cubeQueryOrder,
      limit: 5000,
    },
  };
}

export async function exportCampaignReport({
  clientId,
  userPlatform = "amazon",
  triggeredBy,
  totalCount,
  queryBody,
  visibleColumnKeys,
  isComparison = false,
  setIsExporting = () => {},
  onExportSuccess = () => {},
}) {
  try {
    setIsExporting(true);

    await requestBackendReportExport({
      clientId,
      userPlatform,
      reportType: CAMPAIGN_REPORT_TYPE,
      totalCount,
      triggeredBy,
      queryBody,
      frontendConfig: {
        include_summary_row: true,
        is_comparison: isComparison,
        columns: visibleColumnKeys,
      },
    });

    onExportSuccess();
  } catch (error) {
    console.error(error);
    toast.error(
      error?.response?.data?.message ?? "Failed to submit export request."
    );
  } finally {
    setIsExporting(false);
  }
}
